<template>
 <div class="documents-modal">
  <div class="modal-header">
   <div>
    <h2>Vehicle Documents</h2>
    <div class="subtitle">
     <span>{{ subtitle }}</span>
     <span v-if="statusLabel" class="active-pill" :class="{ 'active-pill--inactive': isInactive }"><span class="dot"></span>{{ statusLabel }}</span>
    </div>
   </div>
   <button type="button" class="close-btn" title="Close" @click="cancelSubmit"><i class="fas fa-times"></i></button>
  </div>

  <div class="modal-body">
   <div class="documents-grid" v-if="vehicleDetails && vehicleDetails.token">
    <DocumentCard
     v-for="doc in documentConfig"
     :key="doc.key"
     :title="doc.title"
     :url="documentField(doc.key, 'image')"
     :status="documentField(doc.key, 'verified')"
     :uploading="uploadingKey === doc.key"
     @preview="previewImage"
     @upload="uploadDocument(doc, $event)"
    ></DocumentCard>
   </div>
   <div v-else class="no-documents">No Data Available</div>
  </div>

  <div class="modal-footer">
   <span>{{ uploadedCount }} of {{ documentConfig.length }} documents uploaded</span>
   <v-btn depressed class="footer-close" @click="cancelSubmit">Close</v-btn>
  </div>

  <!-- Image Preview Dialog -->
  <vue-easy-lightbox :visible="imagePreviewDialog" :imgs="[imageToPreview]" @hide="imagePreviewDialog = false" v-if="!isPdf(imageToPreview)"></vue-easy-lightbox>

  <v-dialog v-model="imagePreviewDialog" class="preview-board" width="700px" max-height="100%" v-if="isPdf(imageToPreview)">
   <iframe width="100%" :src="imageToPreview" style="height: 100vh; overflow: auto"></iframe>
  </v-dialog>
 </div>
</template>

<script>
import axios from "@/axios/axios-global";
import { encryptUtil } from "../../../Utils/decrypt";
import DocumentCard from "@/components/common/DocumentCard.vue";

// key: field in vehicle document, imageName: value the update API expects, expdate: send the stored expiry with the upload
const DOCUMENTS = [
 { key: "rcbook", title: "RC Book", imageName: "rcBook", expdate: true },
 { key: "interior", title: "Interior Photo", imageName: "interior", expdate: false },
 { key: "exterior", title: "Exterior Photo", imageName: "exterior", expdate: false },
 { key: "insurance", title: "Insurance Certificate", imageName: "insurance", expdate: true },
 { key: "carriagepermit", title: "Carriage Permit", imageName: "carriagepermit", expdate: true },
 { key: "tax", title: "Tax Receipt", imageName: "tax", expdate: true },
 { key: "fitness", title: "Fitness Certificate", imageName: "fitness", expdate: true },
 { key: "pollution", title: "PUC Certificate", imageName: "pollution", expdate: true },
];

export default {
 name: "DocumentInsert",
 components: { DocumentCard },
 props: {
  vehicleDetails: Object,
  resetTrigger: String,
 },
 data: () => {
  return {
   documentConfig: DOCUMENTS,
   vehicleDocument: {},
   vehicleInfo: {},
   uploadingKey: "",
   imageToPreview: "",
   imagePreviewDialog: false,
  };
 },
 computed: {
  subtitle() {
   const v = this.vehicleDetails || {};
   const vehicleNo = v.vehicleNo || this.vehicleInfo.vehicleno || "";
   const model = [v.manufacturer, v.model].filter(Boolean).join(" ");
   return [vehicleNo, model, v.category].filter(Boolean).join(" · ");
  },
  isInactive() {
   const v = this.vehicleDetails || {};
   const trash = v.trash !== undefined ? v.trash : this.vehicleInfo.trash;
   return !!trash;
  },
  statusLabel() {
   const v = this.vehicleDetails || {};
   if (v.trash === undefined && this.vehicleInfo.trash === undefined) return "";
   return this.isInactive ? "Inactive" : "Active";
  },
  uploadedCount() {
   return DOCUMENTS.filter((doc) => this.documentField(doc.key, "image")).length;
  },
 },
 mounted() {
  this.getVehicleDetails();
 },
 watch: {
  resetTrigger() {
   this.vehicleDocument = {};
   if (this.resetTrigger == "cab-document-tab") this.getVehicleDetails();
  },
 },
 methods: {
  isPdf(url) {
   return url && url.toLowerCase().includes(".pdf");
  },
  documentField(key, field) {
   const doc = this.vehicleDocument && this.vehicleDocument[key];
   if (!doc) return field === "image" ? "" : null;
   const value = doc[field];
   if (field === "image") return value || "";
   return value === undefined ? null : value;
  },
  async getVehicleDetails() {
   if (this.vehicleDetails && this.vehicleDetails.token) {
    const response = await axios.post("vehicle/details", { vehicleId: this.vehicleDetails.token });
    this.vehicleInfo = response.data || {};
    this.vehicleDocument = (response.data && response.data.document) || {};
   }
  },
  previewImage(imageSrc) {
   this.imageToPreview = imageSrc;
   this.imagePreviewDialog = true;
  },
  async uploadDocument(doc, file) {
   this.uploadingKey = doc.key;
   try {
    const payload = {
     vehicleId: this.vehicleDetails.token,
     imageName: doc.imageName,
    };
    if (doc.expdate) payload.expdate = this.documentField(doc.key, "expdate") || null;

    const formData = new FormData();
    const setencryptUtil = new encryptUtil({ decrypt: payload });
    formData.append("data", setencryptUtil.getEncryptedData());
    formData.append("image", file);
    const response = await axios.patch("vehicle/document/update", formData);
    if (response.status === 200) {
     setTimeout(async () => {
      this.$emit("recallapi");
      await this.getVehicleDetails();
     }, 500);
     this.$toasted.show(response.data.message, { position: "bottom-center", duration: 4000 });
    } else {
     this.$toasted.show(response.data, { position: "bottom-center", duration: 4000 });
    }
   } catch (error) {
    console.error(error);
    this.$toasted.show("Document upload failed. Please try again.", { position: "bottom-center", duration: 4000 });
   } finally {
    this.uploadingKey = "";
   }
  },
  cancelSubmit() {
   this.$emit("cancel-insert");
  },
 },
};
</script>

<style scoped lang="scss">
@import "src/components/common/documentsModal";
</style>
